import { gql } from '../../../core/graphql-client.js';
import { toast } from '../../../core/event-bus.js';
import { confirmDialog } from '../../confirm-dialog.js';
import type { Quarter, Schedule, ScheduleAssignment, Role, Person, Saturday, AvailabilityStatus } from '../../../types.js';

interface RolePersonMap {
  [roleSlug: string]: Person[];
}

interface RoleCompat {
  roleSlug1: string;
  roleSlug2: string;
}

export class ScheduleEditorPage extends HTMLElement {
  private quarter: Quarter | null = null;
  private schedule: Schedule | null = null;
  private roles: Role[] = [];
  private allPeople: Person[] = [];
  private eligiblePeople: RolePersonMap = {};
  // personId_saturdayDate → status
  private availability = new Map<string, AvailabilityStatus>();
  // roleSlug → timeSlot
  private roleTimeSlots = new Map<string, string>();
  // Compatible role pairs
  private roleCompatibilities: RoleCompat[] = [];
  // Highlight person
  private highlightPersonId: number | null = null;

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie grafiku..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const [qData, rData, cData] = await Promise.all([
        gql.query<{ quarters: Quarter[] }>(`
          query { quarters { year quarterNum label isActive saturdays { date eventNote isSkipped sortOrder } } }
        `),
        gql.query<{ roles: Role[] }>(`
          query { roles { slug displayName timeSlot isAutoScheduled sortOrder } }
        `),
        gql.query<{ roleCompatibilities: RoleCompat[] }>(`
          query { roleCompatibilities { roleSlug1 roleSlug2 } }
        `),
      ]);

      this.quarter = qData.quarters.find(q => q.isActive) ?? null;
      this.roles = rData.roles.filter(r => r.isAutoScheduled).sort((a, b) => a.sortOrder - b.sortOrder);
      this.roleCompatibilities = cData.roleCompatibilities;

      // Build timeSlot lookup for all roles
      this.roleTimeSlots.clear();
      for (const r of rData.roles) {
        this.roleTimeSlots.set(r.slug, r.timeSlot);
      }

      if (!this.quarter) {
        this.innerHTML = '<div class="page"><p>Brak aktywnego kwartału.</p></div>';
        return;
      }

      // Load draft schedule, people, availability in parallel
      const [sData, pData, aData] = await Promise.all([
        gql.query<{ schedule: Schedule | null }>(`
          query($year: Int!, $qn: Int!) {
            schedule(year: $year, quarterNum: $qn) {
              quarterYear quarterNum status generatedAt publishedAt
              assignments {
                saturdayDate roleSlug
                saturday { date eventNote isSkipped sortOrder }
                role { slug displayName sortOrder }
                person { id displayName }
                isLocked isManual note
              }
              conflicts { type message saturdayDate roleSlug }
            }
          }
        `, { year: this.quarter.year, qn: this.quarter.quarterNum }),
        gql.query<{ people: Person[] }>(`
          query { people { id displayName isActive roles { slug } } }
        `),
        gql.query<{ availabilityMatrix: { entries: Array<{ personId: number; saturdayDate: string; status: AvailabilityStatus }> } }>(`
          query($year: Int!, $qn: Int!) {
            availabilityMatrix(year: $year, quarterNum: $qn) {
              entries { personId saturdayDate status }
            }
          }
        `, { year: this.quarter.year, qn: this.quarter.quarterNum }),
      ]);

      this.schedule = sData.schedule;

      // Build availability lookup
      this.availability.clear();
      for (const e of aData.availabilityMatrix.entries) {
        this.availability.set(`${e.personId}_${e.saturdayDate}`, e.status);
      }

      // Store all people for highlight dropdown
      this.allPeople = pData.people.filter(p => p.isActive).sort((a, b) => a.displayName.localeCompare(b.displayName));

      // Build eligible people per role
      for (const role of this.roles) {
        this.eligiblePeople[role.slug] = pData.people
          .filter(p => p.isActive && p.roles?.some(r => r.slug === role.slug))
          .sort((a, b) => a.displayName.localeCompare(b.displayName));
      }

      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  /** Check if two roles are compatible */
  private areRolesCompatible(slug1: string, slug2: string): boolean {
    const ts1 = this.roleTimeSlots.get(slug1);
    const ts2 = this.roleTimeSlots.get(slug2);
    if (ts1 !== ts2) return true;

    const r1 = slug1 < slug2 ? slug1 : slug2;
    const r2 = slug1 < slug2 ? slug2 : slug1;
    return this.roleCompatibilities.some(c => c.roleSlug1 === r1 && c.roleSlug2 === r2);
  }

  /** Get warnings for a specific assignment */
  private getWarnings(assignment: ScheduleAssignment): string[] {
    if (!assignment.person || !this.schedule) return [];
    const warnings: string[] = [];
    const personId = assignment.person.id;
    const satDate = assignment.saturday.date;

    // 1. Unavailability check
    const status = this.availability.get(`${personId}_${satDate}`);
    if (status === 'UNAVAILABLE') {
      warnings.push('Osoba niedostępna w tym terminie');
    }

    // 2. Time-slot conflict
    for (const other of this.schedule.assignments) {
      if (other.saturdayDate === assignment.saturdayDate && other.roleSlug === assignment.roleSlug) continue;
      if (other.saturday.date !== satDate) continue;
      if (!other.person || other.person.id !== personId) continue;
      if (!this.areRolesCompatible(assignment.role.slug, other.role.slug)) {
        warnings.push(`Konflikt z: ${other.role.displayName}`);
      }
    }

    return warnings;
  }

  private render(): void {
    const q = this.quarter!;

    if (!this.schedule) {
      this.renderNoSchedule(q);
      return;
    }

    const schedule = this.schedule;
    const saturdays = this.getUniqueSaturdays(schedule.assignments);

    this.innerHTML = `
      <div class="page schedule-page">
        <div class="page-header">
          <h1>Grafik — ${q.label}</h1>
          <div class="header-actions">
            ${schedule.status === 'PUBLISHED' ? '<span class="chip chip-published">Opublikowany</span>' : ''}
            <button class="btn btn-secondary" id="regenerate-btn">Regeneruj</button>
            ${schedule.status === 'DRAFT' ? '<button class="btn btn-primary" id="publish-btn">Publikuj</button>' : ''}
          </div>
        </div>

        ${schedule.conflicts.length > 0 ? `
          <div class="conflicts-banner">
            <strong>Konflikty (${schedule.conflicts.length}):</strong>
            <ul class="conflicts-list">
              ${schedule.conflicts.map(c => `<li>${c.message}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        <div class="schedule-toolbar">
          <label class="highlight-label">
            Podświetl osobę:
            <select id="highlight-person">
              <option value="">— wyłącz —</option>
              ${this.allPeople.map(p => `<option value="${p.id}" ${p.id === this.highlightPersonId ? 'selected' : ''}>${p.displayName}</option>`).join('')}
            </select>
          </label>
        </div>

        <div class="schedule-table-wrap">
          <table class="schedule-table">
            <thead>
              <tr>
                <th class="col-date">Data</th>
                ${this.roles.map(r => `<th class="col-role">${r.displayName}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${saturdays.map(sat => {
                if (sat.isSkipped) return '';
                return `
                  <tr data-date="${sat.date}">
                    <td class="col-date">
                      <span class="date-text">${this.formatDate(sat.date)}</span>
                      ${sat.eventNote ? `<span class="date-note">${sat.eventNote}</span>` : ''}
                    </td>
                    ${this.roles.map(role => {
                      const assignment = schedule.assignments.find(
                        a => a.saturday.date === sat.date && a.role.slug === role.slug
                      );
                      if (!assignment) return '<td class="col-role cell-empty">—</td>';
                      return this.renderCell(assignment, role);
                    }).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      </div>
      <style>
        .schedule-page { max-width: 100%; overflow-x: auto; }
        .header-actions { display: flex; gap: var(--space-sm); }
        .conflicts-banner {
          background: var(--color-error-bg, #fdecea);
          border: 1px solid var(--color-error);
          border-radius: var(--radius-md);
          padding: var(--space-md);
          margin-bottom: var(--space-md);
          font-size: .875rem;
        }
        .conflicts-list { margin: var(--space-xs) 0 0 var(--space-md); padding: 0; }
        .conflicts-list li { margin-bottom: 2px; }
        .schedule-table-wrap { overflow-x: auto; margin: 0 calc(-1 * var(--space-md)); padding: 0 var(--space-md); }
        .schedule-table {
          width: 100%;
          border-collapse: collapse;
          font-size: .8125rem;
        }
        .schedule-table th, .schedule-table td {
          padding: var(--space-sm);
          border: 1px solid var(--color-surface-container);
          text-align: left;
          vertical-align: top;
        }
        .schedule-table th {
          background: var(--color-surface-dim);
          font-weight: 600;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .col-date { white-space: nowrap; min-width: 120px; }
        .col-role { min-width: 130px; }
        .date-text { font-weight: 500; display: block; }
        .date-note { font-size: .75rem; color: var(--color-on-surface-muted); }
        .cell-empty { color: var(--color-on-surface-muted); }
        .cell-wrap { position: relative; }
        .cell-select {
          width: 100%;
          padding: 4px;
          font-size: .8125rem;
          border: 1px solid var(--color-surface-container);
          border-radius: var(--radius-sm, 4px);
          background: var(--color-surface);
        }
        .cell-select.cell-unassigned { border-color: var(--color-error); background: var(--color-error-bg, #fdecea); }
        .cell-select.cell-warning { border-color: var(--color-error); background: var(--color-error-bg, #fdecea); color: var(--color-error); font-weight: 600; }
        .cell-warning-icon { display: none; position: absolute; top: 2px; right: 4px; font-size: .7rem; cursor: help; }
        .cell-wrap.has-warning .cell-warning-icon { display: block; }
        .cell-locked::after { content: '🔒'; position: absolute; top: 2px; right: 4px; font-size: .7rem; }
        .error-text { color: var(--color-error); }
        .schedule-toolbar {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          margin-bottom: var(--space-sm);
          font-size: .85rem;
        }
        .highlight-label {
          display: flex;
          align-items: center;
          gap: var(--space-xs);
        }
        .highlight-label select {
          font-size: .85rem;
          padding: 4px 8px;
        }
        .chip-published {
          background: var(--color-success-bg, #e8f5e9);
          color: var(--color-success, #2e7d32);
          padding: 4px 12px;
          border-radius: var(--radius-full, 999px);
          font-size: .8rem;
          font-weight: 600;
        }
        .row-current { background: var(--color-primary-bg, #e3f2fd); }
        .row-current td { background: inherit; }
        .cell-highlighted {
          outline: 2px solid var(--color-primary);
          outline-offset: -2px;
          border-radius: var(--radius-sm, 4px);
        }
      </style>
    `;

    // Event: generate
    this.querySelector('#regenerate-btn')!.addEventListener('click', () => this.generate());
    // Event: publish
    this.querySelector('#publish-btn')?.addEventListener('click', () => this.publish());

    // Event: assignment change
    this.querySelectorAll('.cell-select').forEach(select => {
      select.addEventListener('change', (e) => {
        const el = e.target as HTMLSelectElement;
        const satDate = el.dataset.saturdayDate!;
        const roleSlug = el.dataset.roleSlug!;
        const personId = el.value ? Number(el.value) : null;
        this.updateAssignment(satDate, roleSlug, personId, el);
      });
    });

    // Highlight current/nearest Saturday
    this.highlightCurrentSaturday();

    // Person highlight dropdown
    this.querySelector('#highlight-person')!.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value;
      this.highlightPersonId = val ? Number(val) : null;
      this.applyPersonHighlight();
    });
    this.applyPersonHighlight();
  }

  private highlightCurrentSaturday(): void {
    const today = new Date().toISOString().split('T')[0];
    const rows = this.querySelectorAll<HTMLElement>('tr[data-date]');
    let closest: HTMLElement | null = null;
    let closestDiff = Infinity;

    for (const row of rows) {
      const date = row.dataset.date!;
      const diff = Math.abs(new Date(date).getTime() - new Date(today).getTime());
      if (diff < closestDiff) {
        closestDiff = diff;
        closest = row;
      }
    }
    if (closest) {
      closest.classList.add('row-current');
    }
  }

  private applyPersonHighlight(): void {
    this.querySelectorAll('.cell-highlighted').forEach(el => el.classList.remove('cell-highlighted'));

    if (!this.highlightPersonId || !this.schedule) return;

    for (const assignment of this.schedule.assignments) {
      if (!assignment.person || assignment.person.id !== this.highlightPersonId) continue;
      const select = this.querySelector(`select[data-saturday-date="${assignment.saturdayDate}"][data-role-slug="${assignment.roleSlug}"]`);
      if (select) {
        const wrap = select.closest('.cell-wrap');
        if (wrap) wrap.classList.add('cell-highlighted');
      }
    }
  }

  private renderCell(assignment: ScheduleAssignment, role: Role): string {
    const people = this.eligiblePeople[role.slug] ?? [];
    const selected = assignment.person?.id ?? '';
    const isUnassigned = !assignment.person;
    const warnings = this.getWarnings(assignment);
    const hasWarning = warnings.length > 0;

    const selectClass = isUnassigned ? 'cell-unassigned' : (hasWarning ? 'cell-warning' : '');
    const wrapClass = hasWarning ? 'has-warning' : '';
    const tooltip = hasWarning ? warnings.join('; ') : '';

    return `
      <td class="col-role ${assignment.isLocked ? 'cell-locked' : ''}">
        <div class="cell-wrap ${wrapClass}" ${tooltip ? `title="${tooltip}"` : ''}>
          <select class="cell-select ${selectClass}"
                  data-saturday-date="${assignment.saturdayDate}"
                  data-role-slug="${role.slug}">
            <option value="">— brak —</option>
            ${people.map(p => {
              const pStatus = this.availability.get(`${p.id}_${assignment.saturday.date}`);
              const unavailMark = pStatus === 'UNAVAILABLE' ? ' ❌' : '';
              return `<option value="${p.id}" ${p.id === selected ? 'selected' : ''}>${p.displayName}${unavailMark}</option>`;
            }).join('')}
          </select>
          <span class="cell-warning-icon" title="${tooltip}">⚠️</span>
        </div>
      </td>
    `;
  }

  private getUniqueSaturdays(assignments: ScheduleAssignment[]): Saturday[] {
    const map = new Map<string, Saturday>();
    for (const a of assignments) {
      if (!map.has(a.saturday.date)) {
        map.set(a.saturday.date, a.saturday);
      }
    }
    return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
  }

  private renderNoSchedule(quarter: Quarter): void {
    this.innerHTML = `
      <div class="page">
        <div class="page-header"><h1>Grafik — ${quarter.label}</h1></div>
        <div class="card">
          <p>Brak wygenerowanego grafiku dla tego kwartału.</p>
          <button class="btn btn-primary" id="generate-btn">Generuj grafik</button>
        </div>
      </div>
    `;
    this.querySelector('#generate-btn')!.addEventListener('click', () => this.generate());
  }

  private async generate(): Promise<void> {
    const ok = await confirmDialog('Generuj grafik', 'Wygenerować nowy grafik? Poprzedni szkic zostanie nadpisany.', 'Generuj');
    if (!ok) return;

    try {
      toast('Generowanie grafiku...', 'info');
      await gql.query(`
        mutation($year: Int!, $qn: Int!) {
          generateSchedule(year: $year, quarterNum: $qn) { quarterYear quarterNum }
        }
      `, { year: this.quarter!.year, qn: this.quarter!.quarterNum });
      toast('Grafik wygenerowany', 'success');
      this.loadData();
    } catch (e) {
      toast(`Błąd generowania: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async publish(): Promise<void> {
    if (!this.schedule) return;
    const ok = await confirmDialog('Publikuj grafik', 'Opublikować grafik? Będzie widoczny dla wszystkich.', 'Publikuj');
    if (!ok) return;

    try {
      await gql.query(`
        mutation($year: Int!, $qn: Int!) { publishSchedule(year: $year, quarterNum: $qn) { quarterYear quarterNum status } }
      `, { year: this.schedule.quarterYear, qn: this.schedule.quarterNum });
      toast('Grafik opublikowany', 'success');
      this.loadData();
    } catch (e) {
      toast(`Błąd publikacji: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async updateAssignment(saturdayDate: string, roleSlug: string, personId: number | null, selectEl: HTMLSelectElement): Promise<void> {
    if (!this.schedule) return;

    try {
      await gql.query(`
        mutation($qy: Int!, $qn: Int!, $sd: String!, $rs: String!, $personId: Int) {
          updateAssignment(quarterYear: $qy, quarterNum: $qn, saturdayDate: $sd, roleSlug: $rs, personId: $personId) { saturdayDate roleSlug }
        }
      `, { qy: this.schedule.quarterYear, qn: this.schedule.quarterNum, sd: saturdayDate, rs: roleSlug, personId });

      // Update local schedule data
      const assignment = this.schedule.assignments.find(
        a => a.saturdayDate === saturdayDate && a.roleSlug === roleSlug
      );
      if (assignment) {
        if (personId) {
          let personName = '';
          for (const people of Object.values(this.eligiblePeople)) {
            const p = people.find(p => p.id === personId);
            if (p) { personName = p.displayName; break; }
          }
          assignment.person = { id: personId, displayName: personName } as Person;
        } else {
          assignment.person = null;
        }
      }

      // Re-validate this cell and all cells for same saturday
      this.revalidateCell(selectEl, assignment!);
      if (assignment) {
        this.revalidateSaturday(assignment.saturday.date);
      }
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private revalidateCell(selectEl: HTMLSelectElement, assignment: ScheduleAssignment): void {
    const wrap = selectEl.closest('.cell-wrap') as HTMLElement;
    if (!wrap) return;

    const warnings = this.getWarnings(assignment);
    const hasWarning = warnings.length > 0;
    const isUnassigned = !assignment.person;
    const tooltip = warnings.join('; ');

    selectEl.classList.toggle('cell-unassigned', isUnassigned && !hasWarning);
    selectEl.classList.toggle('cell-warning', hasWarning);
    wrap.classList.toggle('has-warning', hasWarning);
    wrap.title = tooltip;

    const icon = wrap.querySelector('.cell-warning-icon') as HTMLElement;
    if (icon) icon.title = tooltip;
  }

  private revalidateSaturday(saturdayDate: string): void {
    if (!this.schedule) return;
    for (const assignment of this.schedule.assignments) {
      if (assignment.saturday.date !== saturdayDate) continue;
      const select = this.querySelector(`select[data-saturday-date="${assignment.saturdayDate}"][data-role-slug="${assignment.roleSlug}"]`) as HTMLSelectElement | null;
      if (select) this.revalidateCell(select, assignment);
    }
  }

  private formatDate(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
  }
}

customElements.define('schedule-editor-page', ScheduleEditorPage);
